import React, { useEffect, useRef, useState } from 'react';
import './OwnerAnalytics.css';
import { ArrowLeft } from 'lucide-react';

// Mock analytics data
const mockAnalyticsData = {
  dailyRevenue: 8750,
  totalCustomers: 42,
  avgOrderValue: 208,
  avgTableTimeMinutes: 45,
  popularItems: [
    { name: 'Butter Chicken', orders: 18, revenue: 3420 },
    { name: 'Biryani Special', orders: 15, revenue: 3000 },
    { name: 'Paneer Tikka', orders: 12, revenue: 1800 },
    { name: 'Naan Combo', orders: 8, revenue: 800 },
    { name: 'Masala Dosa', orders: 6, revenue: 900 }
  ]
};

export default function OwnerAnalytics({ onBack }) {
  const [data, setData] = useState(mockAnalyticsData);
  const [loading, setLoading] = useState(true);           // only for first load
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);    // silent background refresh
  const loadedRef = useRef(false);
  const abortRef = useRef(null);

  useEffect(() => {
    let timer;

    const load = async (background = false) => {
      try {
        if (!loadedRef.current && !background) {
          setLoading(true); // show skeleton only once
        } else {
          setRefreshing(true); // background refresh without flicker
        }

        // Mock loading delay
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Use mock data with slight variations on refresh
        const mockData = {
          ...mockAnalyticsData,
          dailyRevenue: mockAnalyticsData.dailyRevenue + Math.floor(Math.random() * 500),
          totalCustomers: mockAnalyticsData.totalCustomers + Math.floor(Math.random() * 5)
        };
        
        setData(mockData);
        setError(null);
        
      } catch (e) {
        if (!loadedRef.current) setError(e.message || 'Failed to load analytics');
      } finally {
        if (!loadedRef.current) {
          setLoading(false);
          loadedRef.current = true;
        }
        setRefreshing(false);
      }
    };

    // initial load
    load(false);

    // background polling (no UI flicker) when tab is visible
    const startInterval = () => {
      timer = setInterval(() => {
        if (!document.hidden) load(true);
      }, 15000); // keep 15s but without triggering loading state
    };

    startInterval();

    const onVisibilityChange = () => {
      if (!document.hidden) {
        // refresh immediately when user returns to tab
        load(true);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const formatINR = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(n || 0));
  const fmtTime = (m) => {
    const mins = Number(m || 0);
    if (mins >= 60) return `${Math.floor(mins/60)}h ${mins%60}m`;
    return `${mins}m`;
  };
  return (
    <div className="oa-container">
      <header className="oa-header">
        {onBack && (
          <button className="oa-back-btn" onClick={onBack} aria-label="Back to Dashboard">
            <ArrowLeft size={20} />
          </button>
        )}
        <div className="oa-header-content">
          <h1 className="oa-title">Analytics</h1>
          <p className="oa-subtitle">Today's Performance{refreshing ? ' • updating…' : ''}</p>
        </div>
      </header>
      <main className="oa-main">
        <section className="oa-section">
          <h2 className="oa-section-title">Key Metrics</h2>
          <div className="oa-stats-grid">
            <div className="oa-stat-card oa-bg-green">
              <div className="oa-stat-value">₹{loading ? '--' : formatINR(data.dailyRevenue)}</div>
              <div className="oa-stat-label">Daily Revenue</div>
            </div>
            <div className="oa-stat-card oa-bg-blue">
              <div className="oa-stat-value">{loading ? '--' : Number(data.totalCustomers || 0)}</div>
              <div className="oa-stat-label">Total Customers</div>
            </div>
            <div className="oa-stat-card oa-bg-amber">
              <div className="oa-stat-value">₹{loading ? '--' : formatINR(data.avgOrderValue)}</div>
              <div className="oa-stat-label">Avg Order Value</div>
            </div>
            <div className="oa-stat-card oa-bg-purple">
              <div className="oa-stat-value">{loading ? '--' : fmtTime(data.avgTableTimeMinutes)}</div>
              <div className="oa-stat-label">Avg Table Time</div>
            </div>
          </div>
        </section>
        <section className="oa-section">
          <h2 className="oa-section-title">Popular Items Today</h2>
          {error && <div style={{color:'#ef4444', padding:'6px 8px'}}>{String(error)}</div>}
          {(data.popularItems || []).slice(0, 3).map((item, idx) => (
            <div key={idx} className="oa-popular-item">
              <div className="oa-item-info">
                <div className="oa-item-name">{item.name}</div>
                <div className="oa-item-orders">{item.orders} orders</div>
              </div>
              <div className="oa-item-rank">{idx + 1}</div>
            </div>
          ))}
          {(!loading && (!data.popularItems || data.popularItems.length === 0)) && (
            <div className="oa-empty">No items sold yet today</div>
          )}
        </section>
      </main>
    </div>
  );
}
